import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useWebRTC, CallType } from "@/hooks/useWebRTC";
import CallOverlay from "@/components/CallOverlay";
import { isConvWatched } from "@/utils/patientCallGuard";
import { getConsultationAccess } from "@/utils/consultationAccess";

interface ConvInfo {
  id: string;
  practitioner_id: string;
  practitionerName: string;
  access: {
    status: string;
    paid: boolean;
    created_at: string;
    consultation_started_at: string | null;
    consultation_expires_at: string | null;
    chat_unlocked_until: string | null;
  };
}

/**
 * Persistent background listener that gives patients the ability to receive
 * calls from their practitioners on any page — not just inside PatientInbox.
 *
 * When PatientInbox already has the specific chat open (and its own useWebRTC
 * subscribed), the guard (patientCallGuard) prevents double-handling.
 */
const PatientCallListener = () => {
  const { user } = useAuth();

  // The conversation that currently has an active or incoming call
  const [activeConvId, setActiveConvId] = useState<string | null>(null);

  const {
    callState, callType, isMuted, isCameraOff, isSpeakerOn,
    incomingCall, localStream, remoteStream,
    answerCall, rejectCall, endCall,
    toggleMute, toggleCamera, toggleSpeaker,
    triggerIncomingCall,
  } = useWebRTC({
    conversationId: activeConvId,
    userId: user?.id ?? null,
    userName: user?.name ?? "Patient",
  });

  const callStateRef = useRef(callState);
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  const triggerRef = useRef(triggerIncomingCall);
  useEffect(() => { triggerRef.current = triggerIncomingCall; }, [triggerIncomingCall]);

  // Pending call info written by channel callbacks, consumed by the effect below
  const pendingCallRef = useRef<{ callerName: string; callType: CallType } | null>(null);

  // ── Subscribe to every accepted patient conversation ──────────────────
  // Channel name MUST match what useWebRTC and ChatBox send signals on: `call-${id}`
  useEffect(() => {
    if (!user) return;

    const channels: ReturnType<typeof supabase.channel>[] = [];

    const load = async () => {
      const { data: convData } = await supabase
        .from("conversations")
        .select("id, practitioner_id, status, paid, created_at, consultation_started_at, consultation_expires_at, chat_unlocked_until")
        .eq("patient_id", user.id)
        .eq("status", "accepted");

      if (!convData || convData.length === 0) return;
      const openConversations = convData.filter((conversation) =>
        getConsultationAccess(conversation) === "open",
      );
      if (openConversations.length === 0) return;

      // Pre-fetch practitioner names so we can show the correct caller name
      const ids = openConversations.map((c) => c.practitioner_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", ids);

      const nameMap: Record<string, string> = {};
      profiles?.forEach((p) => { nameMap[p.user_id] = p.full_name; });

      const convInfos: ConvInfo[] = openConversations.map((c) => ({
        id: c.id,
        practitioner_id: c.practitioner_id,
        practitionerName: nameMap[c.practitioner_id] ?? "Practitioner",
        access: c,
      }));

      convInfos.forEach((conv) => {
         // Calls are broadcast on the exact conversation topic. Using a
         // different topic here means patients never receive provider calls.
         const ch = supabase.channel(`call-${conv.id}`, {
          config: { broadcast: { self: false } },
        });

        ch.on("broadcast", { event: "signal" }, ({ payload }) => {
          const { type, senderId, callerName, callType: ct } =
            payload as Record<string, string>;
          if (senderId === user.id) return;

          if (type === "call-request") {
            if (getConsultationAccess(conv.access) !== "open") return;
            // Skip if we're already in a call
            if (callStateRef.current !== "idle") return;
            // Skip if PatientInbox already has this conversation open —
            // it will handle the call via its own useWebRTC instance
            if (isConvWatched(conv.id)) return;

            const resolvedName = callerName || conv.practitionerName;
            setActiveConvId(conv.id);
            pendingCallRef.current = { callerName: resolvedName, callType: ct as CallType };
          }

          if (type === "call-ended" || type === "call-rejected") {
            if (callStateRef.current !== "connected") {
              setActiveConvId(null);
              pendingCallRef.current = null;
            }
          }
        });

        ch.subscribe();
        channels.push(ch);
      });
    };

    load();

    return () => { channels.forEach((ch) => supabase.removeChannel(ch)); };
  }, [user]);

  // ── When activeConvId is set the useWebRTC channel is now subscribed.
  //    Trigger the incoming call state after a brief delay so the new
  //    channel subscription is established before we expect to handle signals.
  useEffect(() => {
    if (!activeConvId) return;
    if (!pendingCallRef.current) return;
    if (callState !== "idle") return;

    const info = pendingCallRef.current;
    pendingCallRef.current = null;

    const t = setTimeout(() => {
      triggerRef.current(info.callerName, info.callType);
    }, 200);
    return () => clearTimeout(t);
  }, [activeConvId, callState]);

  const handleReject = useCallback(() => {
    rejectCall();
    setTimeout(() => setActiveConvId(null), 300);
  }, [rejectCall]);

  const handleEnd = useCallback(() => {
    endCall();
    setTimeout(() => setActiveConvId(null), 2000);
  }, [endCall]);

  // Don't render anything when idle
  if (callState === "idle" && !incomingCall) return null;

  return (
    <CallOverlay
      callState={callState}
      callType={callType}
      partnerName={incomingCall?.callerName ?? ""}
      isMuted={isMuted}
      isCameraOff={isCameraOff}
      isSpeakerOn={isSpeakerOn}
      incomingCall={incomingCall}
      localStream={localStream}
      remoteStream={remoteStream}
      onAnswer={answerCall}
      onReject={handleReject}
      onEnd={handleEnd}
      onToggleMute={toggleMute}
      onToggleCamera={toggleCamera}
      onToggleSpeaker={toggleSpeaker}
    />
  );
};

export default PatientCallListener;
